import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Search,
  Package,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Clock,
  TrendingUp,
  Apple,
  Sparkles,
  FlaskConical,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

type ProductType = 'food' | 'cosmetic' | 'all';

// Stat Card Component
interface StatCardProps {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
  subtext?: string;
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label, subtext }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-600">{label}</div>
          {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
        </div>
      </div>
    </div>
  );
}

// Section Header Component
interface SectionHeaderProps {
  icon: React.ElementType;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

function SectionHeader({ icon: Icon, title, description, action }: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          {description && <p className="text-sm text-slate-500">{description}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

// Safety level badge
function SafetyBadge({ level }: { level: string }) {
  const config = {
    safe: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Sicher' },
    caution: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Vorsicht' },
    avoid: { bg: 'bg-red-100', text: 'text-red-700', label: 'Vermeiden' },
  }[level] || { bg: 'bg-slate-100', text: 'text-slate-700', label: level };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// Ingredient type
interface Ingredient {
  id: string;
  ingredient_name: string;
  ingredient_aliases: string[];
  category: string;
  risk_level: 'safe' | 'caution' | 'avoid';
  risk_reason_de: string;
  product_type: 'food' | 'cosmetic' | null;
  is_active: boolean;
  created_at: string;
}

// Edit Form
interface IngredientFormProps {
  ingredient?: Ingredient;
  onSave: (data: Partial<Ingredient>) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function IngredientForm({ ingredient, onSave, onCancel, isLoading }: IngredientFormProps) {
  const [name, setName] = useState(ingredient?.ingredient_name || '');
  const [category, setCategory] = useState(ingredient?.category || '');
  const [safetyLevel, setSafetyLevel] = useState<'safe' | 'caution' | 'avoid'>(ingredient?.risk_level || 'caution');
  const [description, setDescription] = useState(ingredient?.risk_reason_de || '');
  const [keywords, setKeywords] = useState(ingredient?.ingredient_aliases?.join(', ') || '');
  const [productType, setProductType] = useState<'food' | 'cosmetic'>(ingredient?.product_type || 'food');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ingredient_name: name.toLowerCase(),
      category,
      risk_level: safetyLevel,
      risk_reason_de: description,
      ingredient_aliases: keywords.split(',').map((k) => k.trim().toLowerCase()).filter((k) => k),
      product_type: productType,
      is_active: true,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">
          {ingredient ? 'Inhaltsstoff bearbeiten' : 'Neuer Inhaltsstoff'}
        </h3>
        <button type="button" onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="z.B. Koffein"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Kategorie *</label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
            placeholder="z.B. Stimulanzien"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Produkttyp *</label>
        <div className="flex gap-3">
          {[
            { value: 'food', label: 'Lebensmittel', icon: Apple, color: 'text-green-600 border-green-500 bg-green-50' },
            { value: 'cosmetic', label: 'Kosmetik', icon: Sparkles, color: 'text-pink-600 border-pink-500 bg-pink-50' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setProductType(option.value as 'food' | 'cosmetic')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                productType === option.value
                  ? option.color
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <option.icon className="w-4 h-4" />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Sicherheitsstufe *</label>
        <div className="flex gap-3">
          {[
            { value: 'safe', label: 'Sicher', icon: CheckCircle, color: 'text-emerald-600 border-emerald-500 bg-emerald-50' },
            { value: 'caution', label: 'Vorsicht', icon: AlertTriangle, color: 'text-amber-600 border-amber-500 bg-amber-50' },
            { value: 'avoid', label: 'Vermeiden', icon: XCircle, color: 'text-red-600 border-red-500 bg-red-50' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setSafetyLevel(option.value as 'safe' | 'caution' | 'avoid')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                safetyLevel === option.value
                  ? option.color
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <option.icon className="w-4 h-4" />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Beschreibung *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          rows={3}
          placeholder="Warum ist dieser Inhaltsstoff bedenklich/sicher?"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-1">Keywords (kommagetrennt)</label>
        <input
          type="text"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="coffein, coffee, kaffee"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
        />
        <p className="text-xs text-slate-400 mt-1">Alternative Namen für besseres Matching</p>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading || !name || !category || !description}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          Speichern
        </button>
      </div>
    </form>
  );
}

export function ProductCheckAdminPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<ProductType>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['product-check-stats'],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [scansResult, productsResult, usersResult, todayResult] = await Promise.all([
        supabase.from('user_product_scans').select('id', { count: 'exact', head: true }),
        supabase.from('products').select('id', { count: 'exact', head: true }),
        supabase.from('user_product_scans').select('user_id'),
        supabase.from('user_product_scans').select('id', { count: 'exact', head: true }).gte('scanned_at', today.toISOString()),
      ]);

      const uniqueUsers = new Set((usersResult.data as { user_id: string }[] | null)?.map((u) => u.user_id));

      return {
        totalScans: scansResult.count || 0,
        cachedProducts: productsResult.count || 0,
        uniqueUsers: uniqueUsers.size,
        scansToday: todayResult.count || 0,
      };
    },
  });

  // Fetch ingredients
  const { data: ingredients, isLoading } = useQuery({
    queryKey: ['ingredient-safety', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ingredient_safety')
        .select('*')
        .eq('locale', contentLocale)
        .order('ingredient_name');

      if (error) throw error;
      return data as Ingredient[];
    },
  });

  // Create ingredient
  const createIngredient = useMutation({
    mutationFn: async (data: Partial<Ingredient>) => {
      const { error } = await (supabase.from('ingredient_safety') as any).insert({ ...data, locale: contentLocale });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredient-safety'] });
      setIsCreating(false);
      toast.success('Inhaltsstoff erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Update ingredient
  const updateIngredient = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Ingredient> }) => {
      const { error } = await (supabase.from('ingredient_safety') as any).update({ ...data, locale: contentLocale }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredient-safety'] });
      setEditingIngredient(null);
      toast.success('Inhaltsstoff aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete ingredient
  const deleteIngredient = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ingredient_safety').delete().eq('id', id).eq('locale', contentLocale);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredient-safety'] });
      toast.success('Inhaltsstoff gelöscht');
    },
  });

  // Filter ingredients
  const filteredIngredients = ingredients?.filter((i) => {
    // Filter by product type
    if (filterType !== 'all' && i.product_type !== filterType) return false;

    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      i.ingredient_name.toLowerCase().includes(query) ||
      i.category?.toLowerCase().includes(query) ||
      i.ingredient_aliases?.some((k) => k.toLowerCase().includes(query))
    );
  });

  // Group by safety level (filtered)
  const safeCount = filteredIngredients?.filter((i) => i.risk_level === 'safe').length || 0;
  const cautionCount = filteredIngredients?.filter((i) => i.risk_level === 'caution').length || 0;
  const avoidCount = filteredIngredients?.filter((i) => i.risk_level === 'avoid').length || 0;

  // Count by product type
  const foodCount = ingredients?.filter((i) => i.product_type === 'food').length || 0;
  const cosmeticCount = ingredients?.filter((i) => i.product_type === 'cosmetic').length || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Produktcheck</h1>
              <p className="text-slate-500">Übersicht und Inhaltsstoff-Datenbank verwalten</p>
            </div>
          </div>
          {/* Quick Action Buttons */}
          <div className="flex items-center gap-3">
            <LocaleSelector />
            <button
              onClick={() => setIsCreating(true)}
              disabled={isCreating || editingIngredient !== null}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
            >
              <FlaskConical className="w-4 h-4" />
              Inhaltsstoff
            </button>
            <Link
              to="/features/product-check/products"
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Package className="w-4 h-4" />
              Produkt
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Search}
          iconBg="bg-orange-100"
          iconColor="text-orange-600"
          value={stats?.totalScans || 0}
          label="Scans gesamt"
        />
        <StatCard
          icon={Package}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          value={stats?.cachedProducts || 0}
          label="Produkte im Cache"
        />
        <StatCard
          icon={Users}
          iconBg="bg-teal-100"
          iconColor="text-teal-600"
          value={stats?.uniqueUsers || 0}
          label="Nutzer"
        />
        <StatCard
          icon={Clock}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          value={stats?.scansToday || 0}
          label="Scans heute"
        />
      </div>

      {/* Ingredient Stats - All 5 in one row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <Apple className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-lg font-bold text-green-700">{foodCount}</div>
            <div className="text-xs text-green-600 truncate">Lebensmittel</div>
          </div>
        </div>
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-lg font-bold text-pink-700">{cosmeticCount}</div>
            <div className="text-xs text-pink-600 truncate">Kosmetik</div>
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-lg font-bold text-emerald-700">{safeCount}</div>
            <div className="text-xs text-emerald-600 truncate">Sicher</div>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-lg font-bold text-amber-700">{cautionCount}</div>
            <div className="text-xs text-amber-600 truncate">Vorsicht</div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-lg font-bold text-red-700">{avoidCount}</div>
            <div className="text-xs text-red-600 truncate">Vermeiden</div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingIngredient) && (
        <IngredientForm
          ingredient={editingIngredient || undefined}
          onSave={(data) => {
            if (editingIngredient) {
              updateIngredient.mutate({ id: editingIngredient.id, data });
            } else {
              createIngredient.mutate(data);
            }
          }}
          onCancel={() => {
            setIsCreating(false);
            setEditingIngredient(null);
          }}
          isLoading={createIngredient.isPending || updateIngredient.isPending}
        />
      )}

      {/* Ingredients List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <SectionHeader
          icon={TrendingUp}
          title="Inhaltsstoff-Datenbank"
          description={`${ingredients?.length || 0} Einträge`}
          action={
            !isCreating && !editingIngredient && (
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Neu
              </button>
            )
          }
        />

        {/* Filter Tabs & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          {/* Product Type Filter */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
            {[
              { value: 'all', label: 'Alle' },
              { value: 'food', label: 'Lebensmittel', icon: Apple },
              { value: 'cosmetic', label: 'Kosmetik', icon: Sparkles },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFilterType(option.value as ProductType)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filterType === option.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.icon && <option.icon className="w-4 h-4" />}
                {option.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suche nach Name, Kategorie oder Keyword..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Typ</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Kategorie</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Stufe</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Beschreibung</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center">
                    <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
                  </td>
                </tr>
              ) : filteredIngredients?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-500">
                    {searchQuery || filterType !== 'all' ? 'Keine Ergebnisse gefunden' : 'Keine Inhaltsstoffe vorhanden'}
                  </td>
                </tr>
              ) : (
                filteredIngredients?.map((ingredient) => (
                  <tr key={ingredient.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-slate-900">{ingredient.ingredient_name}</span>
                    </td>
                    <td className="py-3 px-4">
                      {ingredient.product_type === 'cosmetic' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded-full">
                          <Sparkles className="w-3 h-3" />
                          Kosmetik
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <Apple className="w-3 h-3" />
                          Lebensmittel
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-slate-600">{ingredient.category}</span>
                    </td>
                    <td className="py-3 px-4">
                      <SafetyBadge level={ingredient.risk_level} />
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-slate-600 line-clamp-1">{ingredient.risk_reason_de}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditingIngredient(ingredient)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Diesen Inhaltsstoff wirklich löschen?')) {
                              deleteIngredient.mutate(ingredient.id);
                            }
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
